/* ============
 * Edit Page
 * ============
 *
 * This is the page for adding product bundle.
 */
import { mapMutations, mapGetters } from 'vuex';
import store from './../../../../store';
import Forms from './../../../../utils/forms/forms';
import productService from './../../../../services/product';
import productBundleService from './../../../../services/productBundle';
import productBundleTransformer from './../../../../transformers/productBundle';
import bulkAddProductModal from './../../../../components/bulkAddProductModal/bulkAddProductModal.vue';
import productDepositService from './../../../../services/productDeposit';

export default {
  data() {
    return {
      form: new Forms({
        name: {
          value: '',
          type: 'text',
        },
        description: {
          value: '',
          type: 'textarea',
        },
        price: {
          value: '',
          type: 'text',
          attributes: {
            disabled: true,
          },
        },
      }),
      formTable: new Forms({
        productId: {
          value: '',
          type: 'autocomplete',
          attributes: {
            disabled: true,
            href: 'products/_/edit#productInfo',
          },
        },
        price: {
          value: '',
          type: 'text',
        },
        productDepositName: {
          value: '',
          type: 'plaintext',
        },
        qty: {
          value: '',
          type: 'text',
        },
      }),
      tableData: [],
      groupErrors: [],
    };
  },

  mounted() {
    Promise.all([
      productBundleService.show(store.state.route.params.id),
      productDepositService.all(),
    ])
    .then((res) => {
      this.form.assignData(Object.assign({}, res[0], { productTypeId: null }));
      this.tableData = res[0].productBundleGroup.map(p => ({
        group_id: p.id,
        name: p.name,
        taxId: p.tax_id,
        taxPerc: p.tax_perc,
        bundleId: p.bundle_id,
        price: 0,
        items: [],
      }));
      res[0].products.forEach((p) => {
        const deposit = this.productDeposits.all.find(d => +p.product_deposit_id === +d.id);
        const index = this.tableData.findIndex(p2 => (p2.group_id === p.pivot.group_id));
        this.tableData[index].items.push({ price: p.pivot.price, qty: p.pivot.qty, id: p.id, name: p.name, depositAmount: deposit.amount || 0, productDepositName: deposit.amount ? `${this.$t('static.orders.edit.deposit')}: ${deposit.name}` : '' });
        this.tableData[index].price = this.tableData[index].items.reduce((accumulator, object) => accumulator + (parseFloat(object.price) * object.qty) + (object.depositAmount * object.qty), 0);
      });
    });
  },

  watch: {
    tableData: {
      handler(value) {
        let price = 0;
        value.forEach((p, key) => {
          price += parseFloat(p.price);
          this.tableData[key].price = p.items.reduce((accumulator, object) => accumulator + (parseFloat(object.price) * object.qty) + (object.depositAmount * object.qty), 0);
        });
        this.form.price = price;
      },
      deep: true,
    },
  },

  computed: {
    ...mapGetters({
      products: 'basicProducts',
      productDeposits: 'allProductDeposits',
    }),

    isInOwnRoute() {
      return this.$route.name === 'productBundles.edit';
    },

    list() {
      return this.tableData;
    },
  },

  methods: {
    ...mapMutations({
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalSize: 'SET_MODAL_SIZE',
      setModalProps: 'SET_MODAL_PROPS',
      setModalButtonLoading: 'SET_MODAL_BUTTON_LOADING',
    }),

    numberOnly(e) {
      if (!(/^\d+$/.test(e.key))) {
        e.preventDefault();
      }
    },

    showBulkAddModal() {
      this.setModalContent(() => bulkAddProductModal);
      this.setModalProps({
        fetchProductsMethod: this.fetchAllProductBasic,
        onSave: this.handleBulkSave,
      });
      this.toggleModal(true);
    },

    fetchAllProductBasic() {
      return Promise.all([
        productService.allBasic({
          filter: 'active',
          type: 'product',
        }),
      ]);
    },

    handleBulkSave(selectedProducts) {
      const products = selectedProducts.map(p => {
        const deposit = this.productDeposits.all.find(d => +p.productDepositId === +d.id);
        return {
          id: p.id,
          productId: p.id,
          name: p.name,
          qty: p.qty,
          taxId: p.taxId,
          price: 0,
          depositAmount: deposit.amount || 0,
          productDepositName: deposit.amount ? `${this.$t('static.orders.edit.deposit')}: ${deposit.name}` : '',
          // eslint-disable-next-line comma-dangle
          taxPerc: p.tax_perc
        };
      });
      return Promise.all([])
      .then(() => {
        products.forEach((item) => {
          const index = this.tableData.findIndex(obj => obj.taxId === item.taxId);
          if (index > -1) {
            this.tableData[index].items.push(item);
          } else {
            this.tableData.push({
              group_id: 0,
              taxPerc: item.taxPerc,
              taxId: item.taxId,
              price: 0,
              items: [item],
            });
          }
        });
      });
    },

    destroyTable(id) {
      return Promise.all([])
      .then(() => {
        this.tableData.forEach((obj, key) => {
          const index = obj.items.findIndex(p => p.id === id);
          if (index > -1) {
            this.tableData[key].items.splice(index, 1);
            if (this.tableData[key].items.length === 0) {
              this.tableData.splice(key, 1);
            }
            return;
          }
        });
      });
    },

    updateTable(id) {
      return Promise.all([])
      .then(() => {
        this.tableData.forEach((obj, key) => {
          const index = obj.items.findIndex(p => p.id === id);
          if (index > -1) {
            this.tableData[key].items[index].qty = this.formTable.qty;
            this.tableData[key].items[index].price = this.formTable.price;
            return;
          }
        });
      });
    },

    update() {
      this.form.loading = true;
      this.groupErrors = [];
      const product = productBundleTransformer.send(this.form.data());
      product.bundles = this.tableData;
      Promise.all([
        productBundleService.update(store.state.route.params.id, product),
      ]).then(() => {
        this.form.loading = false;
        this.$message({
          message: this.$t('static.products.edit.success'),
          type: 'success',
        });
      }).catch((errors) => {
        this.form.recordErrors(errors);
        this.form.loading = false;
        this.groupErrors = errors;
      });
    },

    destroy() {
      Promise.all([
        productBundleService.destroy(store.state.route.params.id),
      ])
      .then(() => {
        this.$router.push({
          name: 'productBundles.index',
        });
      });
    },
  },

  components: {
    VForm: require('./../../../../components/form/form.vue'),
    VFormTable: require('./../../../../components/formTable/formTable.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/productBundles/edit/edit.js